import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { postUtm, SearchFormData } from "@blacknut/javascript-sdk/dist";
import { analyticsService } from "@blacknut/javascript-sdk/dist/service/AnalyticsService";
import { State } from "../../redux/reducers";
import { logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";

const whiteListParams = [
  "utm_id",
  "utm_cluster",
  "utm_source",
  "utm_campaign",
  "utm_medium",
  "utm_term",
  "utm_content",
  "ae",
  "bnclid",
  "fbclid",
  "gclid",
];

export const useAnalytics = () => {
  const { user } = useSelector((state: State) => state.globalState);
  useEffect(() => {
    if (user) {
      analyticsService.setUserId(user.id);
    }
    analyticsService.setDeviceScreen(
      `${window.screen.width * window.devicePixelRatio}x${
        window.screen.height * window.devicePixelRatio
      }`,
    );
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) {
      const tzArray = tz.toLowerCase().split("/");
      if (tzArray.length === 2) {
        analyticsService.setTzCountry(tzArray[1]);
        analyticsService.setTzRegion(tzArray[0]);
      }
    }
    analyticsService.enabled = !user?.anonymous;
  }, [user]);
  return {
    trackSignupGoogle: useCallback(() => {
      analyticsService.trackEvent("create_account_google");
    }, []),
    trackSignupEmail: useCallback(() => {
      analyticsService.trackEvent("create_account_email");
    }, []),
    trackSignupAmazon: useCallback(() => {
      analyticsService.trackEvent("create_account_amazon");
    }, []),

    trackSigninGoogle: useCallback(() => {
      analyticsService.trackEvent("login_google");
    }, []),
    trackSigninEmail: useCallback(() => {
      analyticsService.trackEvent("login_email");
    }, []),
    trackSigninAmazon: useCallback(() => {
      analyticsService.trackEvent("login_amazon");
    }, []),

    trackSubscribeGoogle: useCallback(() => {
      analyticsService.trackEvent("subscribed_google");
    }, []),
    trackSubscribeStripe: useCallback(() => {
      analyticsService.trackEvent("subscribed_stripe");
    }, []),
    trackSubscribeAmazon: useCallback(() => {
      analyticsService.trackEvent("subscribed_amazon");
    }, []),

    trackPageView: useCallback((id: string) => {
      analyticsService.trackEvent("catalog", { page_id: id });
    }, []),
    trackSurpriseMe: useCallback(() => {
      analyticsService.trackEvent("surprise_me");
    }, []),
    trackAppOpen: useCallback((params?: Record<string, string>) => {
      if (!params) {
        analyticsService.trackEvent("app_open");
        return;
      }
      const _params = Object.fromEntries(
        Object.entries(params).filter(([key]) => whiteListParams.includes(key)),
      );
      analyticsService.trackEvent("app_open", _params);

      if (!!Object.keys(_params).length) {
        postUtm(_params).subscribe({
          error: (error) => {
            logE(LOGGING_TAG, "Caught error posting tracking param: %o", error);
          },
        });
      }
    }, []),
    trackLastPlayed: useCallback(() => {
      analyticsService.trackEvent("last_played");
    }, []),
    trackFavorites: useCallback(() => {
      analyticsService.trackEvent("favorites");
    }, []),
    trackExplorer: useCallback(() => {
      analyticsService.trackEvent("explorer");
    }, []),
    trackSearchView: useCallback(() => {
      analyticsService.trackEvent("search_view");
    }, []),
    trackSearch: useCallback((data: SearchFormData) => {
      const params: Record<string, string> = {};
      if (data.controllers && data.controllers.length > 0) {
        params.controllers = data.controllers.join(",");
      }
      if (data.genres && data.genres.length > 0) {
        params.genres = data.genres.map((g) => g.id).join(",");
      }
      if (data.multiplayer) {
        params.multiplayer = "1";
      }
      if (data.solo) {
        params.solo = "1";
      }
      if (data.name) {
        params.name = data.name;
      }
      if (data.sessionDurations && data.sessionDurations.length > 0) {
        params.sessionDurations = data.sessionDurations.join(",");
      }
      if (data.age) {
        params.age = data.age.join(",");
      }
      analyticsService.trackEvent("search_result", params);
    }, []),
    trackSearchResultsEmpty: useCallback(() => {
      analyticsService.trackEvent("search_result_empty");
    }, []),
    trackLeMag: useCallback(() => {
      analyticsService.trackEvent("lemag");
    }, []),
    trackSettings: useCallback(() => {
      analyticsService.trackEvent("settings");
    }, []),
    trackGameDetail: useCallback((id: string, passId: string) => {
      analyticsService.trackEvent("game_detail", { game_id: id, pass: passId });
    }, []),
    trackDeeplink: useCallback((deeplink: string) => {
      analyticsService.trackEvent("deeplink", { deeplink });
    }, []),
    trackGamePlay: useCallback((id: string, passId: string) => {
      analyticsService.trackEvent("game_play", {
        game_id: id,
        pass: passId,
      });
    }, []),
    trackPass: useCallback((pass: string, id?: string) => {
      analyticsService.trackEvent("pass", {
        pass,
        ...(id && { game_id: id }),
      });
    }, []),
    trackLg10y: useCallback((id: string) => {
      analyticsService.trackEvent("lg10y", {
        game_id: id,
      });
    }, []),
    trackLemagRead: useCallback((slug: string) => {
      analyticsService.trackEvent("lemag_read", { game_id: slug });
    }, []),
    trackGameFavorite: useCallback((id: string, passId: string) => {
      analyticsService.trackEvent("game_favorite", {
        game_id: id,
        pass: passId,
      });
    }, []),
    trackGamepadCompanion: useCallback((gameId: string) => {
      analyticsService.trackEvent("gamepad_companion", { game_id: gameId });
    }, []),
  };
};
